import React from 'react';
import { Award, Users, Globe, Target, Edit } from 'lucide-react';
import { useApp } from '../../context/AppContext';
import { Link } from 'react-router-dom';

const MemberCard = ({ member }) => {
    const [imgError, setImgError] = React.useState(false);
    const initials = member.name ? member.name.split(' ').map(n => n[0]).join('').slice(0, 2) : '??';

    return (
        <div className="bg-white p-6 rounded-2xl shadow-sm text-center group hover:-translate-y-2 transition-transform duration-300">
            <div className="w-24 h-24 mx-auto bg-gray-100 rounded-full mb-4 overflow-hidden flex items-center justify-center border border-gray-100 shadow-inner">
                {member.image && !imgError ? (
                    <img
                        src={member.image}
                        alt={member.name}
                        className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                        onError={() => setImgError(true)}
                    />
                ) : (
                    <div className="text-2xl font-bold text-primary-400 bg-primary-50 w-full h-full flex items-center justify-center uppercase">
                        {initials}
                    </div>
                )}
            </div>
            <h3 className="text-xl font-semibold text-gray-900">{member.name || "Customer Name"}</h3>
            <p className="text-primary-600 mb-4">{member.role || "Position"}</p>
            <p className="text-gray-500 text-sm">{member.bio || "Bio description..."}</p>
        </div>
    );
};

const About = () => {
    const { siteContent, currentUser } = useApp();
    const { about } = siteContent;

    return (
        <div className="pt-20 pb-16 relative">
            {/* Admin Edit Shortcut */}
            {currentUser?.role === 'admin' && (
                <div className="fixed bottom-6 right-6 z-50">
                    <Link
                        to="/admin/content"
                        className="bg-gray-900 text-white px-6 py-3 rounded-full font-semibold shadow-2xl hover:bg-black transition-all flex items-center gap-2 border border-gray-800"
                    >
                        <Edit className="w-4 h-4" />
                        Edit Content
                    </Link>
                </div>
            )}
            {/* Hero */}
            <section className="bg-primary-900 text-white py-20 relative overflow-hidden">
                <div className="absolute inset-0 bg-[url('https://images.unsplash.com/photo-1556761175-5973dc0f32e7?ixlib=rb-4.0.3&auto=format&fit=crop&w=1932&q=80')] bg-cover bg-center opacity-10"></div>
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10 text-center">
                    <h1 className="text-4xl md:text-6xl font-bold mb-6">{about.title}</h1>
                    <p className="text-xl text-primary-100 max-w-2xl mx-auto">
                        {about.description}
                    </p>
                </div>
            </section>

            {/* Mission & Vision */}
            <section className="py-20">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="grid md:grid-cols-2 gap-12 items-center">
                        <div className="space-y-6">
                            <h2 className="text-3xl font-bold text-gray-900">Our Mission</h2>
                            <p className="text-gray-600 leading-relaxed">
                                {about.mission}
                            </p>
                            <div className="flex gap-4">
                                <div className="p-4 bg-blue-50 rounded-xl">
                                    <Target className="w-8 h-8 text-blue-600 mb-2" />
                                    <h3 className="font-semibold text-gray-900">Goal 2030</h3>
                                    <p className="text-sm text-gray-500">{about.goal}</p>
                                </div>
                                <div className="p-4 bg-green-50 rounded-xl">
                                    <Globe className="w-8 h-8 text-green-600 mb-2" />
                                    <h3 className="font-semibold text-gray-900">Eco Friendly</h3>
                                    <p className="text-sm text-gray-500">{about.eco}</p>
                                </div>
                            </div>
                        </div>
                        <div className="rounded-2xl overflow-hidden shadow-2xl transform rotate-2 hover:rotate-0 transition-transform duration-500">
                            <img src="https://images.unsplash.com/photo-1521737604893-d14cc237f11d?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80" alt="Team Meeting" className="w-full h-full object-cover" />
                        </div>
                    </div>
                </div>
            </section>

            {/* Leadership Team */}
            <section className="py-20 bg-gray-50">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="text-center mb-16">
                        <h2 className="text-3xl font-bold text-gray-900 mb-4">Leadership Team</h2>
                        <p className="text-gray-600">Guided by industry veterans with decades of experience.</p>
                    </div>
                    <div className="grid md:grid-cols-3 gap-8">
                        {about.team && about.team.map((member) => (
                            <MemberCard key={member.id} member={member} />
                        ))}
                    </div>
                </div>
            </section>
        </div>
    );
};

export default About;
